import { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { endOfMonth } from "date-fns";
import InputDateComponent from "presentation/components/Shared/InputDateComponent";
import { translateText } from "infra/translation/translate";

/**
 * Propriedades para o componente VacationSchedulerForm.
 */
interface VacationSchedulerFormProps {
  /**
   * Função de callback que é chamada quando o utilizador clica no botão "Agendar".
   * @param {Date} startDate - A data de início das férias selecionada.
   * @param {Date} endDate - A data de fim das férias selecionada.
   */
  onSchedule: (startDate: Date, endDate: Date) => void;
  /**
   * A data máxima que pode ser selecionada no formulário.
   */
  endDateLimit: Date | undefined;
}

/**
 * `VacationSchedulerForm` é um componente de formulário que permite aos utilizadores
 * agendar um novo período de férias.
 *
 * Funcionalidades:
 * - Permite a seleção de uma data de início e de fim.
 * - Impõe uma regra de negócio que restringe o agendamento de férias ao mesmo mês.
 * - Desativa o botão de agendamento até que ambas as datas sejam válidas.
 * - Invoca um callback `onSchedule` quando o agendamento é submetido.
 *
 * @param {VacationSchedulerFormProps} props - As propriedades do componente.
 * @returns {React.FC} Um componente de formulário para agendamento de férias.
 */
const VacationSchedulerForm: React.FC<VacationSchedulerFormProps> = ({
  onSchedule,
  endDateLimit,
}) => {
  /**
   * Estado local para gerir as datas de início e fim do formulário.
   */
  const [form, setForm] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null,
  });

  /**
   * Efeito que é executado sempre que a data de início (`form.startDate`) é alterada.
   * - Preenche automaticamente a data de fim se ela estiver vazia para uma melhor experiência de utilizador.
   * - Garante que a data de fim não é inválida (anterior à data de início ou para além do mês seguinte).
   */
  useEffect(() => {
    if (!form.startDate) return;

    const nextMonth = new Date(form.startDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const endOfNextMonth = endOfMonth(nextMonth);

    // Se a data de fim não existir, ou se tiver ficado inválida (anterior à data de início
    // ou para além do limite de um mês), atualiza a data de fim para ser igual à de início.
    if (
      !form.endDate ||
      form.endDate < form.startDate ||
      form.endDate > endOfNextMonth
    ) {
      setForm((prev) => ({ ...prev, endDate: form.startDate }));
    }
  }, [form.startDate]);

  /**
   * Manipulador para o clique no botão "Agendar".
   * Invoca o callback `onSchedule` com as datas selecionadas.
   */
  const handleScheduleClick = () => {
    if (form.startDate && form.endDate) {
      onSchedule(form.startDate, form.endDate);
    }
  };

  /**
   * Define a data mínima e máxima permitida para o campo de data de fim,
   * com base na data de início selecionada.
   */
  const endDateMin = form.startDate || new Date();
  const startDateMin = new Date().getMonth() === 0 ? new Date(new Date().getFullYear() -1, 11, 1) : new Date(new Date().getFullYear(), 0, 1);
  const startDateMax = new Date(new Date().getFullYear(), 11, 31);
  const endDateMax = form.startDate
    ? endOfMonth(
        new Date(form.startDate.getFullYear(), 11, 31)
      )
    : endDateLimit;

  return (
    <>
      <div className="timesheet-1-col mt-5">
        <InputDateComponent
          label={translateText("Start date", "pt")}
          fieldName="startDate"
          currentObject={form}
          getValue={form.startDate}
          setValue={(obj) =>
            setForm({ ...form, startDate: new Date(obj.startDate) })
          }
          view="date"
          dateFormat="dd/mm/yy"
          minDate={startDateMin}
          maxDate={startDateMax}
        />
      </div>

      <div className="timesheet-1-col mt-5">
        <InputDateComponent
          label={translateText("End date", "pt")}
          fieldName="endDate"
          currentObject={form}
          getValue={form.endDate ?? null}
          setValue={(obj) =>
            setForm({
              ...form,
              endDate: obj.endDate ? new Date(obj.endDate) : null,
            })
          }
          view="date"
          dateFormat="dd/mm/yy"
          minDate={endDateMin}
          maxDate={endDateMax ?? endDateLimit}
        />
      </div>

      <div className="default-4-cols button-align">
        <Button
          label="Agendar"
          onClick={handleScheduleClick}
          className="button-size"
          disabled={!form.startDate || !form.endDate}
        />
      </div>
    </>
  );
};

export default VacationSchedulerForm;
